/**
 * SubmitDemand — self-service demand submission, open to ANY logged-in user.
 *
 * Hits POST /demands/submit (useSubmitDemand). The submitter supplies CONTENT
 * ONLY (UAT decision 2026-09-11): no focus-area question, no classifier
 * feedback, nothing about internal handling on the page. The server
 * classifies the text and sets the landing itself — a wrong landing is the
 * council's to correct from the audit queue, not the submitter's to prevent.
 * Identity/stage/priority are stamped server-side; the demand lands in `new`.
 * The page still shows WHO it will be stamped as (owner ask, 2026-09-22), so
 * the submitter can confirm it before sending.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeStyles,
  Caption1,
  tokens,
  Body1,
  Button,
  Field,
  Input,
  Textarea,
  Title3,
} from '@fluentui/react-components'
import { DocumentAdd24Regular } from '@fluentui/react-icons'
import { useSubmitDemand } from '../hooks/useDemands'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { UserAvatar } from '../components/UserAvatar'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    background: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  brand: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    color: bayerColors.navy,
    textDecoration: 'none',
  },
  brandLogo: { width: '26px', height: '26px', objectFit: 'contain' },
  root: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: bayerColors.navy,
  },
  intro: { color: tokens.colorNeutralForeground3 },
  submitter: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  submitterText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  submitterName: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  submitterEmail: { color: tokens.colorNeutralForeground3, overflowWrap: 'anywhere' },
  hint: { color: tokens.colorNeutralForeground3 },
  optionalHeader: {
    marginTop: tokens.spacingVerticalM,
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
})

interface SubmitFields {
  title: string
  description: string
  target_user: string
  anticipated_benefit: string
  background: string
  department: string
}

const EMPTY: SubmitFields = {
  title: '',
  description: '',
  target_user: '',
  anticipated_benefit: '',
  background: '',
  department: '',
}

// One-time key for this filling-in of the form (#1264). It has to be minted
// when the form opens and reused across a reload — not minted on click, since
// a reload would then mint a different one, which is the same as having none.
// sessionStorage survives a reload (and is per-tab); a genuine second visit
// mints a fresh key because we clear it on a successful submit.
const IDEMPOTENCY_STORAGE_KEY = 'dac.submitDemand.idempotencyKey'

function currentIdempotencyKey(): string {
  try {
    const existing = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY)
    if (existing) return existing
    const minted = crypto.randomUUID()
    sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, minted)
    return minted
  } catch {
    // Private mode / storage disabled: fall back to a per-instance key. It
    // won't survive a reload, but that only forfeits dedup on that edge — a
    // resubmit is no worse than today, and the double-click guard still holds.
    return crypto.randomUUID()
  }
}

function clearIdempotencyKey(): void {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export function SubmitDemand() {
  const styles = useStyles()
  const navigate = useNavigate()
  const toast = useToast()
  const submit = useSubmitDemand()
  // The identity the server will stamp: under View-as that is the target,
  // because the request carries the impersonation header.
  const me = useEffectiveUser()
  const [f, setF] = useState<SubmitFields>(EMPTY)
  // Stable for this form instance; survives a reload via sessionStorage. Lazy
  // initializer so the key is read/minted once, not on every render.
  const [idempotencyKey] = useState(currentIdempotencyKey)

  function set<K extends keyof SubmitFields>(key: K, value: SubmitFields[K]) {
    setF((prev) => ({ ...prev, [key]: value }))
  }

  const canSubmit = !!(f.title.trim() && f.description.trim())

  // Cancel used to throw away whatever had been typed, without a word.
  function onCancel() {
    const typed = Object.values(f).some((v) => typeof v === 'string' && v.trim())
    if (typed && !window.confirm('Discard what you have written?')) return
    navigate('/')
  }

  function onSubmit() {
    if (!canSubmit || submit.isPending) return
    submit.mutate(
      {
        title: f.title.trim(),
        description: f.description.trim(),
        target_user: f.target_user.trim() || undefined,
        anticipated_benefit: f.anticipated_benefit.trim() || undefined,
        background: f.background.trim() || undefined,
        department: f.department.trim() || undefined,
        // Reused across a reload/retry so the server lands one record (#1264).
        idempotency_key: idempotencyKey,
      },
      {
        onSuccess: (d) => {
          // Retire the key so the next genuine submission from this tab mints a
          // fresh one instead of deduping against the demand just created.
          clearIdempotencyKey()
          // The page they land on says where it went; the toast must not
          // contradict it (#1368).
          toast.showToast({
            title:
              d.disposition === 'digital_council'
                ? 'Demand submitted — it was passed to DSAI.'
                : 'Demand submitted — the council will review it shortly.',
          })
          navigate(`/demand/${d.id}`)
        },
        onError: (err: unknown) => {
          toast.showToast({
            title: apiErrorMessage(err, 'Failed to submit demand'),
            intent: 'error',
          })
        },
      },
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <a
          className={styles.brand}
          href="/"
          onClick={(e) => {
            e.preventDefault()
            onCancel()
          }}
        >
          <img src="/dac-logo.svg" alt="" className={styles.brandLogo} />
          DACcelerate
        </a>
        <Button appearance="subtle" onClick={onCancel} data-testid="open-app-link">
          Open the app →
        </Button>
      </header>
      <div className={styles.root} data-testid="submit-demand-page">
        <div className={styles.header}>
          <DocumentAdd24Regular />
          <Title3>Submit a demand</Title3>
        </div>
        <Body1 className={styles.intro}>
          Describe what you need — the council routes it to the right team.
        </Body1>

        {me && (
          <div className={styles.submitter} data-testid="submit-submitter">
            <span aria-hidden="true">
              <UserAvatar name={me.name} size={36} />
            </span>
            <div className={styles.submitterText}>
              <Caption1 className={styles.hint}>Submitting as</Caption1>
              <span className={styles.submitterName}>{me.name}</span>
              <Caption1 className={styles.submitterEmail}>{me.email}</Caption1>
            </div>
          </div>
        )}

        <Field label="Title" required>
          <Input
            value={f.title}
            onChange={(_, d) => set('title', d.value)}
            placeholder="Short summary of the need"
            maxLength={255}
            autoFocus
            data-testid="submit-title"
          />
        </Field>

        <Field label="What do you need? What's the problem?" required>
          <Textarea
            value={f.description}
            onChange={(_, d) => set('description', d.value)}
            rows={4}
            placeholder="Describe the need or problem you're trying to solve"
            data-testid="submit-description"
          />
        </Field>

        <div className={styles.optionalHeader}>More detail (optional)</div>

        <Field label="Who is it for? (target users)">
          <Input value={f.target_user} onChange={(_, d) => set('target_user', d.value)} />
        </Field>
        <Field label="Anticipated benefit">
          <Textarea
            value={f.anticipated_benefit}
            onChange={(_, d) => set('anticipated_benefit', d.value)}
            rows={2}
          />
        </Field>
        <Field label="Background / context">
          <Textarea
            value={f.background}
            onChange={(_, d) => set('background', d.value)}
            rows={2}
          />
        </Field>
        <Field label="Department">
          <Input value={f.department} onChange={(_, d) => set('department', d.value)} />
        </Field>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!canSubmit || submit.isPending}
            onClick={onSubmit}
            data-testid="submit-demand-btn"
          >
            {submit.isPending ? 'Submitting…' : 'Submit demand'}
          </Button>
          <Button
            appearance="subtle"
            onClick={onCancel}
            disabled={submit.isPending}
            data-testid="submit-cancel"
          >
            Cancel
          </Button>
        </div>
        {/* A disabled button that does not say what it is waiting for is a
            dead end for whoever cannot see why (#1374). */}
        {!canSubmit && (
          <Caption1 className={styles.hint} data-testid="submit-hint">
            Add a title and describe what you need — then you can submit.
          </Caption1>
        )}
      </div>
    </div>
  )
}
